import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ABTest } from '@freelancer/abtest';
import { Auth } from '@freelancer/auth';
import { ContestUpgradesHelper } from '@freelancer/contest-upgrades';
import {
  BackendPushResponse,
  Datastore,
  LOGGED_OUT_KEY,
  notEmpty,
  RequestStatus,
  startWithEmptyList,
} from '@freelancer/datastore';
import { BackendErrorTypes } from '@freelancer/datastore/backend-error.types';
import {
  ContestBudgetRange,
  ContestBudgetRangesCollection,
  ContestFee,
  ContestFeesCollection,
  ContestMinimumBudgetClassification,
  ContestMinimumBudgetClassificationsCollection,
  ContestsCollection,
  ContestUpgrade,
  ContestViewContestsCollection,
  CurrenciesCollection,
  Currency,
  Equipment,
  EquipmentCollection,
  JobClassifierCategory,
  JobClassifierCollection,
  Language,
  LanguageDetect,
  LanguageDetectCollection,
  LanguagesCollection,
  PostJobPageFormState,
  ProjectBudgetOption,
  ProjectBudgetOptionsCollection,
  ProjectUpgradeFees,
  ProjectUpgradeFeesCollection,
  ProjectViewProjectsCollection,
  Skill,
  SkillBundlesCollection,
  SkillsCollection,
  UserCalifornianStatusesCollection,
  UsersCollection,
  UsersLocationCollection,
} from '@freelancer/datastore/collections';
import { Drafts } from '@freelancer/drafts';
import { Localization } from '@freelancer/localization';
import {
  PartialPaymentsCartItem,
  PaymentsCart,
} from '@freelancer/payments-cart';
import { PreloadRoute } from '@freelancer/preload-route';
import { ProjectUpgradesHelper } from '@freelancer/project-upgrades';
import { TimeUtils } from '@freelancer/time-utils';
import { Tracking } from '@freelancer/tracking';
import { ModalService } from '@freelancer/ui';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { Margin } from '@freelancer/ui/margin';
import { ModalSize } from '@freelancer/ui/modal';
import { SelectItem } from '@freelancer/ui/select';
import { FontColor, FontType, FontWeight, TextSize } from '@freelancer/ui/text';
import {
  getUpgradeName,
  transformUpgradeType,
  UpgradeType,
} from '@freelancer/ui/upgrade-tag';
import {
  containsContactDetailsRegex,
  currentOrFutureDate,
  dateFormat,
  dirtyAndValidate,
  maxLength,
  maxValue,
  minLength,
  minValue,
  noBlacklistTerms,
  notPattern,
  required,
  requiredTruthy,
  wholeNumber,
} from '@freelancer/ui/validators';
import { assertNever, isDefined, isNumeric, toNumber } from '@freelancer/utils';
import {
  ESCROW_COM_CURRENCIESApi,
  RoleApi,
  TimeUnitApi,
} from 'api-typings/common/common';
import { ContestTypeApi } from 'api-typings/contests/contests';
import { ContextTypeApi, DestinationApi } from 'api-typings/payments/payments';
import { LoginSignupModalComponent } from 'app/login-signup/login-signup-modal/login-signup-modal.component';
import { LoginOrSignup } from 'app/login-signup/login-signup-modal/login-signup-modal.model';
import * as Rx from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  publishReplay,
  refCount,
  scan,
  skipWhile,
  startWith,
  switchMap,
  switchMapTo,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { BudgetType } from '../project-budget/budget-type.component';
import { CUSTOM_BUDGET_ID } from '../project-budget/budget.component';
import {
  DEFAULT_CONTEST_DURATION,
  DurationType,
  URGENT_CONTEST_DURATION,
} from './contest/duration-type/duration-type.component';
import {
  DESCRIPTION_MAX_CHARACTERS,
  DESCRIPTION_MIN_CHARACTERS,
} from './description/description.component';
import { JobType } from './job-type/job-type.component';
import { ProjectAdvancedUpgrades } from './project/upgrades/upgrades.component';

interface Step {
  name: StepName;
  visited: boolean;
  show: boolean;
}

export enum StepName {
  TITLE_AND_DESCRIPTION = 'title',
  SKILLS = 'skills',
  JOB_TYPE = 'jobType',
  PROJECT_IS_LOCAL = 'isLocal',
  PROJECT_LOCATION = 'location',
  PROJECT_DATE = 'projectDate',
  PROJECT_EQUIPMENT = 'projectEquipment',
  PROJECT_BUDGET_TYPE = 'budgetType',
  PROJECT_BUDGET = 'projectBudget',
  PROJECT_UPGRADES = 'projectUpgrades',
  PROJECT_SUMMARY = 'projectSummary',
  CONTEST_BUDGET = 'contestBudget',
  CONTEST_DURATION_TYPE = 'durationType',
  CONTEST_DURATION = 'duration',
  CONTEST_UPGRADES = 'contestUpgrades',
  CONTEST_SUMMARY = 'contestSummary',
}

export interface FormSubmitApiError {
  readonly contextId?: number;
  readonly errorCode:
    | BackendErrorTypes['contest_create']
    | BackendErrorTypes['project_create']
    | 'BLOCKED_CALIFORNIAN_ERROR'
    | 'CART_PAYMENT_ERROR';
  readonly requestId?: string;
}

/**
 * Default steps will be constructed on init. All steps will be added to viewState.steps
 * with all steps visited and show = false except for the first step (title and description)
 */
type Steps = { [key in StepName]: Step };

interface ViewState {
  currentStep: Step; // current step is useful to determine what's the next step and whether to show next button or not
  steps: Steps;
  isLocalSkillSelected: boolean; // Is at least 1 skill local skill?
  isAllLocalSkillsSelected: boolean; // Are all skills local skills?
  isUrgentSkillSelected: boolean; // Are any of the skills categorises as Urgent for Contest?
}

interface StepValue {
  stepName: StepName;
  value?: boolean | string;
}
interface ProjectBudgetState {
  fixed?: ProjectBudgetOption;
  hourly?: ProjectBudgetOption;
}

/**
 * The form can only have 2 actions:
 * 1. CHANGE - when value of the form is changed.
 * 2. NEXT - when the user wants to get the next step.
 * Please note when the user is changing the value of the form control
 * on the current step by selecting a card, CHANGE and NEXT are called.
 */
enum FormAction {
  CHANGE,
  NEXT,
}

enum ShortcutKey {
  CTRL_ENTER,
  ENTER,
}

enum SkillCategoryId {
  URGENT = 3,
}

const DEFAULT_FORM_VALUES = {
  budgetType: undefined,
  contestBudget: 190, // Default budget for USD, which is the default currency.
  contestType: null,
  contestUpgrades: {
    featured: false,
    guaranteed: false,
    highlight: false,
    nda: false,
    private: false,
    sealed: false,
    topContest: false,
    urgent: false,
  },
  currencyId: 1,
  description: '',
  duration: DEFAULT_CONTEST_DURATION,
  durationType: '',
  isLocal: null,
  jobType: '',
  location: null,
  endLocation: null,
  projectBudget: undefined,
  projectEquipment: undefined,
  projectUpgrades: {
    featured: false,
    nda: false,
    private: false,
    recruiter: false,
    urgent: false,
    ipContract: false,
    sealed: false,
  },
  projectDate: undefined,
  projectType: null,
  skills: [],
  title: '',
  hasBillingCode: false,
  billingCode: '',
};

// Based on ContestProjectHandler.php::MAX_CONTEST_PRIZE
const MAX_CONTEST_BUDGET = 999999999;

// This is arbitrary as we don't actually have a restriction on the max value
// of a project budget from a product perspective. This value is the maximum
// that JS can handle without losing precision.
const MAX_PROJECT_BUDGET = Number.MAX_SAFE_INTEGER;

@Component({
  selector: 'app-form',
  template: `
    <form [formGroup]="formGroup">
      <ng-container *ngIf="showDraftBanner$ | async">
        <fl-banner-alert
          *ngIf="draftSubmitDate$ | async as submitDate"
          flTrackingSection="PJPDraftBanner"
          flTrackingLabel="Discard"
          i18n="Post project banner alert informing of an existing draft"
          [bannerAlertButton]="{ text: 'Discard' }"
          [closeable]="false"
          [flMarginBottom]="Margin.LARGE"
          [type]="BannerAlertType.INFO"
          (buttonClicked)="handleDiscardDraft()"
        >
          <ng-container
            [ngSwitch]="formGroup.controls[StepName.JOB_TYPE].value"
          >
            <ng-container *ngSwitchCase="JobType.CONTEST">
              We've automatically loaded a contest you were working on
            </ng-container>
            <ng-container *ngSwitchDefault>
              We've automatically loaded a project you were working on
            </ng-container>
          </ng-container>
          <fl-relative-time
            [color]="FontColor.INHERIT"
            [date]="submitDate"
            [includeSeconds]="false"
            [size]="TextSize.INHERIT"
            [strict]="true"
          ></fl-relative-time
          >. You can continue working on it below. Pressing 'Discard' will clear
          the form and remove the draft forever.
        </fl-banner-alert>
      </ng-container>
      <fl-banner-alert
        *ngIf="!(canRetrieveProjectDetails$ | async)"
        i18n="Warning banner when the project details fail to load"
        [closeable]="false"
        [flMarginBottom]="Margin.LARGE"
        [type]="BannerAlertType.WARNING"
      >
        We couldn't fetch this project's details. You may continue to post your
        own project.
      </fl-banner-alert>
      <fl-banner-alert
        *ngIf="hasRepostContestError$ | async"
        i18n="Warning banner when the contest details fail to load"
        [closeable]="false"
        [flMarginBottom]="Margin.LARGE"
        [type]="BannerAlertType.WARNING"
      >
        We couldn't fetch this contest's details. You may continue to post your
        own contest.
      </fl-banner-alert>
      <app-title
        *ngIf="shouldShowView(StepName.TITLE_AND_DESCRIPTION)"
        flTrackingSection="PJPTitle"
        id="{{ StepName.TITLE_AND_DESCRIPTION }}"
        [control]="formGroup.get(StepName.TITLE_AND_DESCRIPTION)"
        [jobType]="formGroup.controls[StepName.JOB_TYPE].value"
        [placeholder]="titlePlaceholder"
        [showSpinner$]="showSpinner$"
      ></app-title>
      <app-description
        *ngIf="shouldShowView(StepName.TITLE_AND_DESCRIPTION)"
        flTrackingSection="PJPDescription"
        [control]="formGroup.get('description')"
        [detectedLanguage$]="detectedLanguage$"
        [fraudReviewsControl]="formGroup.get('descriptionFraudReviews')"
        [jobType]="formGroup.controls[StepName.JOB_TYPE].value"
        [languageControl]="formGroup.get('language')"
        [languages$]="languages$"
        (keyup.control.enter)="
          handleNextAction({ stepName: StepName.TITLE_AND_DESCRIPTION })
        "
      ></app-description>
      <app-file-upload
        *ngIf="shouldShowView(StepName.TITLE_AND_DESCRIPTION)"
        flTrackingSection="PJPFiles"
        [control]="formGroup.get('files')"
        (uploadedFilesChange)="handleUploadedFilesChange($event)"
      >
      </app-file-upload>
      <app-skills
        flTrackingSection="PJPSkills"
        id="{{ StepName.SKILLS }}"
        [control]="formGroup.get(StepName.SKILLS)"
        [description$]="description$"
        [hidden]="!shouldShowView(StepName.SKILLS)"
        [jobType]="formGroup.controls[StepName.JOB_TYPE].value"
        [skills$]="skills$"
        [title$]="title$"
      ></app-skills>
      <app-job-type
        flTrackingSection="PJPJobType"
        id="{{ StepName.JOB_TYPE }}"
        [hidden]="!shouldShowView(StepName.JOB_TYPE)"
        [control]="formGroup.get(StepName.JOB_TYPE)"
        [jobClassification]="jobClassification$ | async"
        (cardSelected)="
          handleNextAction({
            stepName: StepName.JOB_TYPE,
            value: $event
          })
        "
      ></app-job-type>
      <app-project
        [budgets$]="projectBudgetOptions$"
        [budgetsStatus$]="projectBudgetOptionsStatus$"
        [currenciesStatus$]="currenciesStatus$"
        [currencyOptions$]="currencyOptions$"
        [formGroup]="formGroup"
        [selectedCurrency$]="selectedCurrency$"
        [showFreeRecruiterUpgradeBanner]="
          showFreeRecruiterUpgradeBanner$ | async
        "
        [showLocalQuestion]="shouldShowView(StepName.PROJECT_IS_LOCAL)"
        [showLocation]="shouldShowView(StepName.PROJECT_LOCATION)"
        [showProjectBudgetType]="shouldShowView(StepName.PROJECT_BUDGET_TYPE)"
        [showProjectBudget]="shouldShowView(StepName.PROJECT_BUDGET)"
        [showProjectDate]="shouldShowView(StepName.PROJECT_DATE)"
        [showProjectEquipment]="shouldShowView(StepName.PROJECT_EQUIPMENT)"
        [showProjectUpgrades]="shouldShowView(StepName.PROJECT_UPGRADES)"
        [upgradeFees$]="projectUpgradeFees$"
        [upgradeFeesStatus$]="projectUpgradeFeesStatus$"
        (cardSelected)="handleNextAction($event)"
        (advancedUpgradesClicked)="handleProjectAdvancedUpgradesClicked($event)"
      ></app-project>
      <app-contest
        [budgetRange$]="contestBudgetRange$"
        [budgetRangeStatus$]="contestBudgetRangeStatus$"
        [contestMinimumBudgetClassification$]="
          contestMinimumBudgetClassification$
        "
        [currenciesStatus$]="currenciesStatus$"
        [currencyOptions$]="currencyOptions$"
        [formGroup]="formGroup"
        [selectedCurrency$]="selectedCurrency$"
        [showContestBudget]="shouldShowView(StepName.CONTEST_BUDGET)"
        [showContestDuration]="shouldShowView(StepName.CONTEST_DURATION)"
        [showContestUpgrades]="shouldShowView(StepName.CONTEST_UPGRADES)"
        [showDurationType]="shouldShowView(StepName.CONTEST_DURATION_TYPE)"
        [upgradeFees$]="contestUpgradeFees$"
        [upgradeFeesStatus$]="contestUpgradeFeesStatus$"
        (cardSelected)="handleNextAction($event)"
        (advancedUpgradesClicked)="handleContestAdvancedUpgradesClicked($event)"
      ></app-contest>
      <fl-bit
        id="{{
          shouldShowView(StepName.PROJECT_SUMMARY)
            ? StepName.PROJECT_SUMMARY
            : StepName.CONTEST_SUMMARY
        }}"
      >
        <app-summary
          *ngIf="
            shouldShowView(StepName.PROJECT_SUMMARY) ||
            shouldShowView(StepName.CONTEST_SUMMARY)
          "
          [currency]="selectedCurrency$ | async"
          [formGroup]="formGroup"
        ></app-summary>
        <app-submit
          flTrackingSection="PJPPostCTA"
          [error$]="formSubmitApiError$"
          [hidden]="
            !shouldShowView(StepName.PROJECT_SUMMARY) &&
            !shouldShowView(StepName.CONTEST_SUMMARY)
          "
          [isSubmitting]="
            (responsePromise && !(responsePromise | async)) ||
            (responsePromise | async)?.status == 'success'
          "
          [jobType]="formGroup.controls[StepName.JOB_TYPE].value"
          [selectedCurrency]="selectedCurrency$ | async"
          [selectedUpgrades]="selectedUpgrades$ | async"
          [taxType]="(projectUpgradeFees$ | async)?.taxType"
          [totalFee]="totalFee$ | async"
          (onSubmit)="handleSubmit()"
        ></app-submit>
      </fl-bit>
    </form>
    <fl-bit class="NextButton" *ngIf="shouldShowNextButton()">
      <fl-button
        i18n="Next button for wizard steps"
        flTrackingLabel="GoToNextStep-{{ viewState.currentStep.name }}"
        [color]="ButtonColor.SECONDARY"
        [disabled]="!isStepValid(viewState.currentStep.name)"
        [flMarginRight]="Margin.SMALL"
        [size]="ButtonSize.SMALL"
        (click)="handleNextAction({ stepName: viewState.currentStep.name })"
      >
        Next
      </fl-button>
      <fl-text
        [color]="
          isStepValid(viewState.currentStep.name)
            ? FontColor.DARK
            : FontColor.MID
        "
        [flHideMobile]="true"
        [ngSwitch]="getNextButtonShortcut()"
        [weight]="FontWeight.BOLD"
      >
        <ng-container
          *ngSwitchCase="ShortcutKey.CTRL_ENTER"
          i18n="Keyboard combination for going to next step"
        >
          Press CTRL + ENTER
        </ng-container>
        <ng-container
          *ngSwitchCase="ShortcutKey.ENTER"
          i18n="Keyboard combination for going to next step"
        >
          Press ENTER
        </ng-container>
      </fl-text>
    </fl-bit>
  `,
  styleUrls: ['./form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormComponent implements OnInit, OnDestroy {
  BannerAlertType = BannerAlertType;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  FontColor = FontColor;
  TextSize = TextSize;
  FontType = FontType;
  FontWeight = FontWeight;
  Margin = Margin;
  StepName = StepName;
  ShortcutKey = ShortcutKey;
  JobType = JobType;

  @Input() titlePlaceholder?: string;
  @Output() disableForm = new EventEmitter<boolean>();

  readonly craigslistTerms: ReadonlyArray<string> = [
    'craigslist',
    '(^|\\W)cl($|\\W)',
    'clist',
    'adnuke',
    'anonypay',
  ];

  readonly craigslistViolationTerms: ReadonlyArray<string> = [
    'ghost',
    'ghosting',
    'flag',
    'flagging',
    'block',
    'blocking',
    'pva',
    'pvas',
    'phone verified accounts',
    'auto reply',
    'auto respond',
    'post',
    'poster',
    'posting',
    'autopost',
    'autoposter',
    'autoposting',
    'anti-flagging',
    'captcha',
    'captchas',
  ];

  readonly copyrightViolationTerms: ReadonlyArray<string> = [
    'clone',
    'exact copy',
  ];

  fileIds: ReadonlyArray<number> = [];

  formGroup = this.fb.group({
    title: [
      DEFAULT_FORM_VALUES.title,
      [
        required('Please enter a name'),
        minLength(10, 'Please enter at least 10 characters'),
        maxLength(255, 'Please enter at most 255 characters'),
        notPattern(
          containsContactDetailsRegex,
          'Please choose a name without contact details.',
        ),
      ],
    ],
    description: [
      DEFAULT_FORM_VALUES.description,
      [
        required('Please enter a description'),
        minLength(
          DESCRIPTION_MIN_CHARACTERS,
          `Please enter at least ${DESCRIPTION_MIN_CHARACTERS} characters`,
        ),
        maxLength(
          DESCRIPTION_MAX_CHARACTERS,
          `Please enter at most ${DESCRIPTION_MAX_CHARACTERS} characters`,
        ),
        notPattern(
          containsContactDetailsRegex,
          'Please enter a description without contact details',
        ),
        noBlacklistTerms(
          [this.craigslistTerms, this.craigslistViolationTerms],
          "Please ensure that this description follows Craigslist's Terms of Use",
          'CRAIGSLIST_TOU_ERROR',
        ),
        noBlacklistTerms(
          [this.copyrightViolationTerms],
          'Please ensure that this description does not violate intellectual property rights',
          'COPYRIGHT_ERROR',
        ),
      ],
    ],
    descriptionFraudReviews: [
      { value: false, disabled: true },
      [requiredTruthy('Please confirm in order to proceed')],
    ],
    language: [this.localization.languageCode],
    files: [],
    hasBillingCode: [DEFAULT_FORM_VALUES.hasBillingCode],
    billingCode: [
      { value: DEFAULT_FORM_VALUES.billingCode, disabled: true },
      [
        required('Please enter a billing code'),
        maxLength(24, 'Please limit billing code to 24 characters'),
      ],
    ],
    [StepName.SKILLS]: [
      DEFAULT_FORM_VALUES.skills,
      [
        minLength(1, 'Please enter at least 1 skill'),
        maxLength(5, 'Please enter at most 5 skills'),
      ],
    ],
    [StepName.JOB_TYPE]: [
      DEFAULT_FORM_VALUES.jobType,
      [required('Please choose a job type')],
    ],
    [StepName.PROJECT_IS_LOCAL]: [
      DEFAULT_FORM_VALUES.isLocal,
      [required('Please choose between local or non-local project type')],
    ],
    [StepName.PROJECT_LOCATION]: [
      DEFAULT_FORM_VALUES.location,
      [required('Please enter a location')],
    ],
    endLocation: [DEFAULT_FORM_VALUES.endLocation],
    [StepName.PROJECT_BUDGET_TYPE]: [
      DEFAULT_FORM_VALUES.budgetType,
      [required('Please choose a project budget type.')],
    ],
    [StepName.PROJECT_EQUIPMENT]: [
      DEFAULT_FORM_VALUES.projectEquipment,
      [maxLength(10, 'Please enter at most 10 pieces of equipment')],
    ],
    [StepName.CONTEST_DURATION_TYPE]: [
      DEFAULT_FORM_VALUES.durationType,
      [required('Please choose a duration type.')],
    ],
    [StepName.PROJECT_UPGRADES]: this.fb.group(
      DEFAULT_FORM_VALUES.projectUpgrades,
    ),
    [StepName.CONTEST_UPGRADES]: this.fb.group(
      DEFAULT_FORM_VALUES.contestUpgrades,
    ),
    [StepName.CONTEST_DURATION]: [
      DEFAULT_FORM_VALUES.duration,
      [
        required('Please enter a contest duration'),
        minValue(3, 'Enter from 3 to 30 days'),
        maxValue(30, 'Enter from 3 to 30 days'),
        wholeNumber('Please enter a whole number from 3 to 30'),
      ],
    ],
    [StepName.PROJECT_BUDGET]: [
      DEFAULT_FORM_VALUES.projectBudget,
      [required('Please select a project budget')],
    ],
    [StepName.PROJECT_DATE]: [
      DEFAULT_FORM_VALUES.projectDate,
      [
        dateFormat('Please enter a valid date'),
        currentOrFutureDate('You cannot enter a past date'),
      ],
    ],
    // TODO: Complete custom budget validation
    customBudget: this.fb.group({
      minimum: [
        undefined,
        [
          required('Please enter a budget'),
          wholeNumber('The budget must be in whole numbers'),
          maxValue(
            MAX_PROJECT_BUDGET,
            `The budget cannot be greater than ${MAX_PROJECT_BUDGET}`,
          ),
        ],
      ],
      maximum: [
        undefined,
        [
          required('Please enter a budget'),
          wholeNumber('The budget must be in whole numbers'),
          maxValue(
            MAX_PROJECT_BUDGET,
            `The budget cannot be greater than ${MAX_PROJECT_BUDGET}`,
          ),
        ],
      ],
    }),
    currencyId: [
      DEFAULT_FORM_VALUES.currencyId,
      [required('Please select a currency for the budget')],
    ],
    [StepName.CONTEST_BUDGET]: [
      DEFAULT_FORM_VALUES.contestBudget,
      [
        required('Please enter a prize'),
        wholeNumber('The prize must be in whole numbers'),
        maxValue(
          MAX_CONTEST_BUDGET,
          `The prize cannot be greater than ${MAX_CONTEST_BUDGET}`,
        ),
      ],
    ],
    contestType: [DEFAULT_FORM_VALUES.contestType],
    projectType: [DEFAULT_FORM_VALUES.projectType],
  });

  title$: Rx.Observable<string>;
  description$: Rx.Observable<string>;

  is5EyesUser: Promise<boolean | undefined>;

  currencies$: Rx.Observable<ReadonlyArray<Currency>>;
  currenciesStatus$: Rx.Observable<RequestStatus<CurrenciesCollection>>;
  currencyOptions$: Rx.Observable<ReadonlyArray<SelectItem>>;
  selectedCurrency$: Rx.Observable<Currency>;

  isCalifornianUserUsingUnsupportedCurrency$: Rx.Observable<boolean>;

  projectBudgetOptions$: Rx.Observable<ReadonlyArray<ProjectBudgetOption>>;
  projectBudgetOptionsStatus$: Rx.Observable<
    RequestStatus<ProjectBudgetOptionsCollection>
  >;

  jobClassification$: Rx.Observable<JobClassifierCategory>;
  selectedSkills$: Rx.Observable<ReadonlyArray<Skill>>;
  skills$: Rx.Observable<ReadonlyArray<Skill>>;

  projectUpgradeFees$: Rx.Observable<ProjectUpgradeFees>;
  projectUpgradeFeesStatus$: Rx.Observable<
    RequestStatus<ProjectUpgradeFeesCollection>
  >;

  contestUpgradeFees$: Rx.Observable<ContestFee>;
  contestUpgradeFeesStatus$: Rx.Observable<
    RequestStatus<ContestFeesCollection>
  >;

  contestMinimumBudgetClassification$: Rx.Observable<
    ContestMinimumBudgetClassification
  >;
  contestBudgetRange$: Rx.Observable<ContestBudgetRange>;
  contestBudgetRangeStatus$: Rx.Observable<
    RequestStatus<ContestBudgetRangesCollection>
  >;

  formSubmitApiError$: Rx.Observable<FormSubmitApiError | null>;
  selectedUpgrades$: Rx.Observable<ReadonlyArray<string>>;
  totalFee$: Rx.Observable<number>;

  draftSubmitDate$: Rx.Observable<number>;
  enableDraft$: Rx.Observable<boolean>;

  canRetrieveProjectDetails$: Rx.Observable<boolean>;
  showDraftBanner$: Rx.Observable<boolean>;
  showFreeRecruiterUpgradeBanner$: Rx.Observable<boolean>;
  showSpinner$: Rx.Observable<boolean>;

  detectedLanguage$: Rx.Observable<LanguageDetect>;
  detectedLanguageErrorTrackingSubscription?: Rx.Subscription;
  languages$: Rx.Observable<ReadonlyArray<Language>>;

  hasRepostContestError$: Rx.Observable<boolean>;

  billingCodeSubscription?: Rx.Subscription;

  private discardDraftClicked$: Rx.Observable<void>;
  private discardDraftClickedSubject$: Rx.Subject<void>;

  private contestBudgetValidationSubscription?: Rx.Subscription;
  private contestMinimumBudgetClassificationSubscription?: Rx.Subscription;
  private customBudgetValidationSubscription?: Rx.Subscription;

  private defaultContestBudgetSubscription?: Rx.Subscription;
  private selectedProjectBudgetRangeSubscription?: Rx.Subscription;

  private defaultCurrencySubscription?: Rx.Subscription;
  private draftCreateOrUpdateSubscription?: Rx.Subscription;

  private freeUpgradeSubscription?: Rx.Subscription;
  private formGroupSubscription?: Rx.Subscription;
  private isProjectLocalSubscription?: Rx.Subscription;
  private prefilledFormSubscription?: Rx.Subscription;
  private prefilledSkillsSubscription?: Rx.Subscription;
  private prefilledTitleSubscription?: Rx.Subscription;
  private urgentSkillsSubscription?: Rx.Subscription;

  private formSubmitApiErrorSubject$: Rx.BehaviorSubject<FormSubmitApiError | null>;

  private draft$: Rx.Observable<PostJobPageFormState>;
  private userId$: Rx.Observable<string>;

  private readonly DEFAULT_PROJECT_BUDGET_RANGE = 2;

  viewState: ViewState;
  projectAdvancedUpgradeShowNextButton = false;
  contestAdvancedUpgradeShowNextButton = false;

  responsePromise: Promise<
    BackendPushResponse<ProjectViewProjectsCollection | ContestsCollection>
  >;

  constructor(
    @Inject(DOCUMENT) private document: HTMLDocument,
    private abTest: ABTest,
    private auth: Auth,
    private cart: PaymentsCart,
    private contestUpgradesHelper: ContestUpgradesHelper,
    private datastore: Datastore,
    private drafts: Drafts,
    private fb: FormBuilder,
    private localization: Localization,
    private modalService: ModalService,
    private preloadRoute: PreloadRoute,
    private projectUpgradesHelper: ProjectUpgradesHelper,
    private route: ActivatedRoute,
    private router: Router,
    private timeUtils: TimeUtils,
    private tracking: Tracking,
  ) {}

  ngOnInit() {
    this.discardDraftClickedSubject$ = new Rx.Subject<void>();
    this.discardDraftClicked$ = this.discardDraftClickedSubject$.asObservable();

    this.formSubmitApiErrorSubject$ = new Rx.BehaviorSubject<FormSubmitApiError | null>(
      null,
    );
    this.formSubmitApiError$ = this.formSubmitApiErrorSubject$.asObservable();

    this.title$ = this.formGroup.controls.title.valueChanges.pipe(
      publishReplay(1),
      refCount(),
    );

    this.description$ = this.formGroup.controls.description.valueChanges.pipe(
      publishReplay(1),
      refCount(),
    );

    this.languages$ = this.datastore
      .collection<LanguagesCollection>('languages', query =>
        query.where('active', '==', true),
      )
      .valueChanges();

    const validLanguageDetectDescription$ = this.description$.pipe(
      this.timeUtils.rxDebounceTime(1000),
      withLatestFrom(this.languages$.pipe(notEmpty())),
      filter(
        ([description, languages]) =>
          description.length >= DESCRIPTION_MIN_CHARACTERS &&
          !this.formGroup.controls.language.touched,
      ),
      map(([description, _]) => description),
    );

    const detectedLanguageCollection = this.datastore.collection<
      LanguageDetectCollection
    >('languageDetect', query =>
      query.where('text', '==', validLanguageDetectDescription$),
    );

    this.detectedLanguage$ = detectedLanguageCollection.valueChanges().pipe(
      notEmpty(),
      map(languages => languages[0]),
      filter(detectedLanguage => detectedLanguage.likelihood > 0.1),
      tap(language => {
        this.tracking.trackCustomEvent(
          'DetectedNewLanguage',
          'PostProjectForm',
          {
            code: language.code,
          },
        );
      }),
      skipWhile(
        detectedLanguage =>
          detectedLanguage.code === this.localization.languageCode,
      ),
      tap(detectedLanguage => {
        this.formGroup.controls.language.setValue(detectedLanguage.code);
      }),
    );

    this.detectedLanguageErrorTrackingSubscription = detectedLanguageCollection.status$
      .pipe(filter(status => !status.ready && !!status.error))
      .subscribe(status => {
        this.tracking.trackCustomEvent(
          'LanguageDetectionError',
          'PostProjectForm',
          {
            error: status.error
              ? `${status.error.errorCode}`
              : 'LANGUAGE_DETECT_ERROR',
          },
        );
      });

    this.billingCodeSubscription = this.formGroup.controls.hasBillingCode.valueChanges.subscribe(
      hasBillingCode => {
        this.formGroup.controls.billingCode.markAsPristine();
        this.formGroup.controls.billingCode.markAsUntouched();
        if (hasBillingCode) {
          this.formGroup.controls.billingCode.enable();
        } else {
          this.formGroup.controls.billingCode.disable();
        }
      },
    );

    this.userId$ = this.auth.authState$.pipe(
      map(auth => (auth ? auth.userId : LOGGED_OUT_KEY)),
    );

    const userCalifornianStatusDocument = this.datastore.document<
      UserCalifornianStatusesCollection
    >('userCalifornianStatuses', this.userId$);

    const isUserConfirmedCalifornian$ = Rx.combineLatest([
      userCalifornianStatusDocument.status$,
      userCalifornianStatusDocument
        .valueChanges()
        .pipe(map(californianStatus => californianStatus.isUserCalifornian)),
    ]).pipe(
      filter(([status]) => status.ready && !isDefined(status.error)),
      map(([, isUserCalifornian]) => isUserCalifornian),
      startWith(false),
      distinctUntilChanged(),
      publishReplay(1),
      refCount(),
    );

    const currencyListCollection = this.datastore.collection<
      CurrenciesCollection
    >('currencies');

    this.currencies$ = currencyListCollection.valueChanges();
    this.currenciesStatus$ = currencyListCollection.status$;

    // TODO: Error handling if currency options cannot be loaded / fetched
    this.currencyOptions$ = Rx.combineLatest([
      this.currencies$,
      isUserConfirmedCalifornian$,
    ]).pipe(
      map(([options, isUserConfirmedCalifornian]) =>
        options.map(option => ({
          value: option.id,
          displayText: option.code,
          disabled:
            isUserConfirmedCalifornian &&
            !ESCROW_COM_CURRENCIESApi.includes(option.code),
        })),
      ),
    );

    const loggedInUserDoc = this.datastore.document<UsersCollection>(
      'users',
      this.auth.getUserId(),
    );

    // I needed to use a legit user which was why I used `this.auth.getUserId()`
    // instead of `userId$`
    this.is5EyesUser = this.datastore
      .document<UsersLocationCollection>('usersLocation', this.auth.getUserId())
      .valueChanges()
      .pipe(
        map(
          usersLocation =>
            usersLocation.location.country &&
            [
              'United States',
              'United Kingdom',
              'Australia',
              'New Zealand',
              'Canada',
            ].includes(usersLocation.location.country.name),
        ),
        take(1),
      )
      .toPromise();

    const loggedInUserCurrency$ = loggedInUserDoc.valueChanges().pipe(
      map(user => user.currency),
      filter(isDefined),
    );

    const loggedInUserCurrencyId$ = loggedInUserCurrency$.pipe(
      map(currency => currency.id),
    );

    // Set initial selected currency
    // TODO: Handle default if logged out (depends on domain)
    const defaultCurrency$ = Rx.combineLatest([
      loggedInUserCurrencyId$,
      this.currencies$,
      this.discardDraftClicked$.pipe(startWith(undefined)),
    ]).pipe(
      filter(() => !this.formGroup.controls.currencyId.dirty),
      map(([userCurrencyId, currencies]) => {
        const userCurrency = currencies.find(
          currency => currency.id === userCurrencyId,
        );

        if (!userCurrency) {
          // Default to USD
          return currencies.find(currency => currency.id === 1);
        }

        return userCurrency;
      }),
      filter(isDefined),
      tap(currency => this.formGroup.controls.currencyId.setValue(currency.id)),
    );

    this.defaultCurrencySubscription = defaultCurrency$.subscribe();

    this.selectedCurrency$ = Rx.combineLatest([
      this.formGroup.controls.currencyId.valueChanges.pipe(
        startWith(this.formGroup.controls.currencyId.value),
        map(toNumber),
      ),
      this.currencies$,
    ]).pipe(
      map(([currencyId, currencies]) =>
        currencies.find(currency => currency.id === currencyId),
      ),
      filter(isDefined),
      publishReplay(1),
      refCount(),
    );

    this.isCalifornianUserUsingUnsupportedCurrency$ = Rx.combineLatest([
      this.selectedCurrency$,
      isUserConfirmedCalifornian$,
    ]).pipe(
      map(
        ([selectedCurrency, isUserConfirmedCalifornian]) =>
          isUserConfirmedCalifornian &&
          !ESCROW_COM_CURRENCIESApi.includes(selectedCurrency.code),
      ),
    );

    const selectedCurrencyId$ = this.selectedCurrency$.pipe(
      map(currency => currency.id),
      distinctUntilChanged(),
      publishReplay(1),
      refCount(),
    );

    const projectBudgetOptionsCollection = this.datastore.collection<
      ProjectBudgetOptionsCollection
    >('projectBudgetOptions', query =>
      query
        .where('currencyId', '==', selectedCurrencyId$)
        .where(
          'projectType',
          '==',
          this.formGroup.controls[
            StepName.PROJECT_BUDGET_TYPE
          ].valueChanges.pipe(startWith(BudgetType.FIXED), filter(isDefined)),
        ),
    );

    this.projectBudgetOptions$ = projectBudgetOptionsCollection
      .valueChanges()
      .pipe(
        map(options => {
          if (options && options.length > 0) {
            return [
              ...options,
              {
                currencyId: options[0].currencyId,
                id: CUSTOM_BUDGET_ID,
                name: 'Customize budget',
                maximum: 0,
                minimum: 0,
                projectType: options[0].projectType,
              },
            ];
          }

          return options;
        }),
      );
    this.projectBudgetOptionsStatus$ = projectBudgetOptionsCollection.status$;

    const projectBudgetPreviousOption$ = Rx.merge(
      this.formGroup.controls[StepName.PROJECT_BUDGET].valueChanges,
      this.discardDraftClicked$.pipe(map(() => ({}))),
    ).pipe(
      filter(isDefined),
      scan((state: ProjectBudgetState, current: ProjectBudgetOption) =>
        current.projectType
          ? {
              ...state,
              [current.projectType]: current,
            }
          : {},
      ),
      startWith({} as ProjectBudgetState),
    );

    this.selectedProjectBudgetRangeSubscription = Rx.merge(
      this.projectBudgetOptions$,
      this.discardDraftClicked$.pipe(switchMapTo(this.projectBudgetOptions$)),
    )
      .pipe(
        notEmpty(),
        withLatestFrom(projectBudgetPreviousOption$),
        tap(([options, state]) => {
          const budgetType = options[0].projectType;
          const saved = state[budgetType];
          const previous = options.find(
            option => !!saved && option.name === saved.name,
          );
          const defaultRange =
            options[this.DEFAULT_PROJECT_BUDGET_RANGE] || options[0];
          const select = previous || defaultRange;

          this.formGroup.controls[StepName.PROJECT_BUDGET].setValue(select);
        }),
      )
      .subscribe();

    const contestBudgetRangesCollection = this.datastore.collection<
      ContestBudgetRangesCollection
    >('contestBudgetRanges');

    this.contestBudgetRangeStatus$ = contestBudgetRangesCollection.status$;

    this.contestBudgetRange$ = Rx.combineLatest([
      contestBudgetRangesCollection.valueChanges(),
      selectedCurrencyId$,
    ]).pipe(
      map(([budgetRanges, currencyId]) =>
        budgetRanges.find(budgetRange => budgetRange.currencyId === currencyId),
      ),
      filter(isDefined),
    );

    this.contestBudgetValidationSubscription = Rx.combineLatest([
      contestBudgetRangesCollection.status$,
      this.contestBudgetRange$,
    ])
      .pipe(
        tap(([status, budgetRange]) => {
          let validators = [
            required('Please enter a prize'),
            wholeNumber('The prize must be in whole numbers'),
            maxValue(
              MAX_CONTEST_BUDGET,
              `The prize cannot be greater than ${MAX_CONTEST_BUDGET}`,
            ),
          ];

          if (!status.error) {
            validators = [
              ...validators,
              minValue(
                budgetRange.minimum,
                `Minimum prize is ${budgetRange.minimum}.`,
              ),
            ];
          }

          this.formGroup.controls.contestBudget.setValidators(validators);
          this.formGroup.controls.contestBudget.updateValueAndValidity();
        }),
      )
      .subscribe();

    const minBudgetControl = this.formGroup.controls.customBudget.get(
      'minimum',
    ) as FormControl;

    const maxBudgetControl = this.formGroup.controls.customBudget.get(
      'maximum',
    ) as FormControl;

    const minBudgetValidation$ = Rx.combineLatest([
      this.projectBudgetOptions$,
      this.projectBudgetOptionsStatus$,
    ]).pipe(
      tap(([budgetOptions, status]) => {
        let validators = [
          required('Please enter a budget'),
          wholeNumber('The budget must be in whole numbers'),
          maxValue(
            MAX_PROJECT_BUDGET,
            `The budget cannot be greater than ${MAX_PROJECT_BUDGET}`,
          ),
        ];

        if (!status.error && budgetOptions.length > 0) {
          const validMinimumBudget = budgetOptions[0].minimum;

          validators = [
            ...validators,
            minValue(
              validMinimumBudget,
              `Minimum budget is ${validMinimumBudget}`,
            ),
          ];
        }

        minBudgetControl.setValidators(validators);
        minBudgetControl.updateValueAndValidity();

        if (minBudgetControl.value) {
          minBudgetControl.markAsDirty();
        }
      }),
    );

    this.customBudgetValidationSubscription = Rx.combineLatest([
      minBudgetValidation$,
      minBudgetControl.valueChanges.pipe(map(toNumber), filter(isDefined)),
    ])
      .pipe(
        tap(([[budgetOptions, status], minBudget]) => {
          let validators = [
            required('Please enter a budget'),
            wholeNumber('The budget must be in whole numbers'),
            maxValue(
              MAX_PROJECT_BUDGET,
              `The budget cannot be greater than ${MAX_PROJECT_BUDGET}`,
            ),
          ];

          if (!status.error && budgetOptions.length > 0) {
            const validMinimumBudget =
              Math.max(minBudget, budgetOptions[0].minimum) + 1;

            validators = [
              ...validators,
              minValue(
                validMinimumBudget,
                `Maximum budget should be greater than or equal to ${validMinimumBudget}`,
              ),
            ];
          }

          maxBudgetControl.setValidators(validators);
          maxBudgetControl.updateValueAndValidity();

          if (maxBudgetControl.value) {
            maxBudgetControl.markAsDirty();
          }
        }),
      )
      .subscribe();

    this.defaultContestBudgetSubscription = Rx.merge(
      Rx.combineLatest([this.contestBudgetRange$, defaultCurrency$]).pipe(
        filter(() => !this.formGroup.controls.contestBudget.dirty),
        take(1),
        map(([budgetRange]) => budgetRange),
      ),
      this.discardDraftClicked$.pipe(
        switchMapTo(this.contestBudgetRange$),
        take(1),
      ),
    )
      .pipe(
        tap(budgetRange =>
          this.formGroup.controls.contestBudget.setValue(
            budgetRange.defaultAmount,
          ),
        ),
      )
      .subscribe();

    this.skills$ = this.datastore
      .collection<SkillsCollection>('skills')
      .valueChanges()
      .pipe(startWithEmptyList());

    this.selectedSkills$ = Rx.combineLatest([
      this.skills$,
      this.formGroup.controls[StepName.SKILLS].valueChanges as Rx.Observable<
        ReadonlyArray<Skill>
      >,
    ]).pipe(
      map(([skills, selectedSkills]) => {
        const selectedSkillIds = selectedSkills.map(skill => skill.id);
        return skills.filter(skill => selectedSkillIds.includes(skill.id));
      }),
    );

    this.isProjectLocalSubscription = Rx.combineLatest([
      this.selectedSkills$,
      this.formGroup.controls[StepName.PROJECT_LOCATION].valueChanges.pipe(
        startWith(this.formGroup.controls[StepName.PROJECT_LOCATION].value),
        filter(isDefined),
      ),
    ])
      .pipe(
        filter(
          () =>
            this.formGroup.controls[StepName.JOB_TYPE].value ===
            JobType.PROJECT,
        ),
        map(
          ([skills]) =>
            this.areAllSkillsLocal(skills) ||
            (this.hasLocalSkill(skills) &&
              this.formGroup.controls[StepName.PROJECT_IS_LOCAL].value),
        ),
        tap(isLocal => {
          this.formGroup.controls[StepName.PROJECT_IS_LOCAL].setValue(isLocal);
        }),
      )
      .subscribe();

    this.showFreeRecruiterUpgradeBanner$ = this.formGroup.controls[
      StepName.PROJECT_IS_LOCAL
    ].valueChanges.pipe(
      startWith(this.formGroup.controls[StepName.PROJECT_IS_LOCAL].value),
      tap(isLocal => {
        const projectUpgradesForm = this.formGroup.controls[
          StepName.PROJECT_UPGRADES
        ];
        const upgrades = projectUpgradesForm.value;

        // Local projects will be assisted by our recruiters by default
        // Set Recruiter upgrade to false because it has a fee
        if (isLocal && upgrades && upgrades.recruiter) {
          projectUpgradesForm.patchValue({ recruiter: false });
        }
      }),
      publishReplay(1),
      refCount(),
    );
    const debouncedSkillIds$ = this.selectedSkills$.pipe(
      this.timeUtils.rxDebounceTime(700),
      distinctUntilChanged(),
      notEmpty(),
      map(skills => skills.map((skill: Skill) => skill.id)),
    );

    const jobClassifierCollection = this.datastore.collection<
      JobClassifierCollection
    >('jobClassifier', query =>
      query.where('skillIds', '==', debouncedSkillIds$),
    );

    const jobClassifier$ = jobClassifierCollection.valueChanges();

    this.jobClassification$ = Rx.merge(
      Rx.combineLatest([jobClassifier$, jobClassifierCollection.status$]).pipe(
        map(([classifiers, status]) =>
          !status.error && classifiers && classifiers[0]
            ? classifiers[0].category
            : JobClassifierCategory.UNKNOWN,
        ),
      ),
      this.discardDraftClicked$.pipe(map(() => JobClassifierCategory.UNKNOWN)),
    );

    const currencyIds$ = currencyListCollection
      .valueChanges()
      .pipe(map(currencies => currencies.map(currency => currency.id)));

    const contestMinimumBudgetClassifications = this.datastore.collection<
      ContestMinimumBudgetClassificationsCollection
    >('contestMinimumBudgetClassifications', query =>
      query.where('currencyId', 'in', currencyIds$),
    );

    this.contestMinimumBudgetClassification$ = Rx.combineLatest([
      contestMinimumBudgetClassifications.valueChanges(),
      selectedCurrencyId$,
    ]).pipe(
      map(([classifications, currencyId]) =>
        classifications.find(
          classification => classification.currencyId === currencyId,
        ),
      ),
      filter(isDefined),
    );

    this.contestMinimumBudgetClassificationSubscription = this.contestMinimumBudgetClassification$.subscribe();

    const projectUpgradeFeesCollection = this.datastore.collection<
      ProjectUpgradeFeesCollection
    >('projectUpgradeFees', query =>
      query.where('currencyId', '==', selectedCurrencyId$),
    );

    this.projectUpgradeFees$ = projectUpgradeFeesCollection.valueChanges().pipe(
      notEmpty(),
      map(feesList => feesList[0]),
    );

    this.projectUpgradeFeesStatus$ = projectUpgradeFeesCollection.status$;

    const contestUpgradeFeesCollection = this.datastore.collection<
      ContestFeesCollection
    >('contestFees', query =>
      query.where('currencyId', '==', selectedCurrencyId$),
    );

    this.contestUpgradeFees$ = contestUpgradeFeesCollection
      .valueChanges()
      .pipe(map(feesList => feesList[0]));

    this.contestUpgradeFeesStatus$ = contestUpgradeFeesCollection.status$;

    this.urgentSkillsSubscription = this.selectedSkills$.subscribe(skills => {
      if (!this.hasUrgentSkill(skills)) {
        this.formGroup.controls[StepName.CONTEST_DURATION_TYPE].setValue(null);
        this.formGroup.controls[StepName.CONTEST_UPGRADES].patchValue({
          urgent: false,
        });
      }
    });

    // TODO: error handling on getting total upgrades cost
    const totalProjectFee$ = Rx.combineLatest([
      this.formGroup.controls[StepName.PROJECT_UPGRADES].valueChanges,
      this.projectUpgradeFees$,
    ]).pipe(
      map(
        ([projectUpgrades, projectUpgradeFees]: [
          ProjectAdvancedUpgrades,
          ProjectUpgradeFees,
        ]) => {
          const selectedUpgrades = this.getSelectedUpgrades(projectUpgrades)
            .map(upgradeName => transformUpgradeType(upgradeName))
            .filter((upgrade): upgrade is UpgradeType => !!upgrade);

          return this.projectUpgradesHelper.getTotalUpgradesFee(
            selectedUpgrades,
            projectUpgradeFees,
          );
        },
      ),
    );

    // TODO: error handling on getting total upgrades cost
    const totalContestFee$ = Rx.combineLatest([
      this.formGroup.controls[StepName.CONTEST_UPGRADES].valueChanges,
      this.formGroup.controls[StepName.CONTEST_BUDGET].valueChanges,
      this.contestUpgradeFees$,
    ]).pipe(
      map(([contestUpgrades, contestPrize, contestUpgradeFees]) => {
        const selectedUpgrades = Object.entries(contestUpgrades)
          .filter(
            ([upgradeName, isUpgradeSelected]) =>
              isUpgradeSelected && upgradeName !== UpgradeType.GUARANTEED,
          )
          .map(([upgradeName, _]) => transformUpgradeType(upgradeName))
          .filter((upgrade): upgrade is UpgradeType => !!upgrade);

        return (
          this.contestUpgradesHelper.getTotalUpgradesFee(
            selectedUpgrades,
            contestUpgradeFees,
          ) + toNumber(contestPrize)
        );
      }),
    );

    this.totalFee$ = Rx.combineLatest([
      this.formGroup.controls[StepName.JOB_TYPE].valueChanges,
      totalProjectFee$.pipe(startWith(0)),
      totalContestFee$.pipe(startWith(0)),
    ]).pipe(
      map(([jobType, totalProjectFee, totalContestFee]) =>
        jobType === JobType.CONTEST ? totalContestFee : totalProjectFee,
      ),
      publishReplay(1),
      refCount(),
    );

    this.selectedUpgrades$ = this.formGroup.controls[
      StepName.PROJECT_UPGRADES
    ].valueChanges.pipe(
      map(projectUpgrades => this.getSelectedUpgrades(projectUpgrades)),
    );

    this.formGroupSubscription = Rx.combineLatest([
      this.formGroup.valueChanges,
      this.selectedSkills$.pipe(startWith([])),
    ])
      .pipe(
        distinctUntilChanged(([xFormValue], [yFormValue]) =>
          Object.entries(xFormValue).every(
            ([key, value]) => value === yFormValue[key],
          ),
        ),
      )
      .subscribe(([, selectedSkills]) => {
        this.viewState = this.getNewViewState(
          this.viewState,
          this.formGroup,
          FormAction.CHANGE,
          selectedSkills,
        );
        this.formSubmitApiErrorSubject$.next(null);
      });

    this.viewState = this.generateViewStateSkeleton();

    const repostContestId$ = this.route.queryParamMap.pipe(
      map(queryParam => queryParam.get('repost_contest_id')),
      filter(isNumeric),
      startWith(undefined),
    );

    const repostContestDoc = this.datastore.document<
      ContestViewContestsCollection
    >('contestViewContests', repostContestId$.pipe(filter(isDefined)));

    const repostContest$: Rx.Observable<Partial<
      PostJobPageFormState
    >> = repostContestDoc.valueChanges().pipe(
      filter(() => this.formGroup.pristine && this.formGroup.untouched),
      take(1),
      map(contest => ({
        contestPrize: contest.prize,
        currencyId: contest.currency.id,
        description: contest.description,
        jobType: JobType.CONTEST,
        skills: contest.skills,
        title: contest.title,
      })),
      publishReplay(1),
      refCount(),
    );

    this.hasRepostContestError$ = repostContestDoc.status$.pipe(
      map(status => isDefined(status.error)),
    );

    const repostProjectId$ = this.route.queryParamMap.pipe(
      withLatestFrom(repostContestId$),
      filter(([, repostContestId]) => !repostContestId),
      map(([queryParam]) => queryParam.get('repost_project_id')),
      filter(isNumeric),
      startWith(undefined),
    );

    const repostProjectDoc = this.datastore.document<
      ProjectViewProjectsCollection
    >('projectViewProjects', repostProjectId$.pipe(filter(isDefined)));

    const repostProject$: Rx.Observable<Partial<
      PostJobPageFormState
    >> = repostProjectDoc.valueChanges().pipe(
      filter(() => this.formGroup.pristine && this.formGroup.untouched),
      take(1),
      map(project => ({
        description: project.description,
        skills: project.skills,
        title: project.title,
      })),
      publishReplay(1),
      refCount(),
    );

    const hasRepostProjectError$ = repostProjectDoc.status$.pipe(
      map(status => isDefined(status.error)),
    );

    const hasNoRepostId$ = Rx.combineLatest([
      repostProjectId$,
      repostContestId$,
    ]).pipe(
      map(
        ([repostProjectId, repostContestId]) =>
          !(repostProjectId || repostContestId),
      ),
    );

    const titleFromUrl$ = this.route.queryParamMap.pipe(
      withLatestFrom(hasNoRepostId$),
      filter(([, hasNoRepostId]) => hasNoRepostId),
      map(([queryParam]) => queryParam.get('project_title')),
      filter(isDefined),
    );

    const skillsFromUrl$ = this.route.queryParamMap.pipe(
      withLatestFrom(hasNoRepostId$),
      filter(([, hasNoRepostId]) => hasNoRepostId),
      map(([queryParam]) =>
        queryParam.getAll('job_id').map(id => toNumber(id)),
      ),
      filter(ids => ids.every(isNumeric)),
      notEmpty(),
    );

    const category$ = this.route.queryParamMap.pipe(
      withLatestFrom(hasNoRepostId$),
      filter(([, hasNoRepostId]) => hasNoRepostId),
      map(([queryParam]) => queryParam.get('skill_category')),
      filter(isNumeric),
      map(param => toNumber(param)),
    );

    const subcategory$ = this.route.queryParamMap.pipe(
      withLatestFrom(hasNoRepostId$),
      filter(([, hasNoRepostId]) => hasNoRepostId),
      map(([queryParam]) => queryParam.get('skill_subcategory')),
      filter(isNumeric),
      map(param => toNumber(param)),
    );

    const skillBundlesCollection = this.datastore.collection<
      SkillBundlesCollection
    >('skillBundles', query =>
      query
        .where('category', '==', category$)
        .where('subcategory', '==', subcategory$),
    );

    const skillBundles$ = skillBundlesCollection.valueChanges();

    const hasSkillBundlesError$ = skillBundlesCollection.status$.pipe(
      map(status => isDefined(status.error)),
    );

    const projectTemplateTitle$ = Rx.race(
      titleFromUrl$,
      skillBundles$.pipe(map(bundles => bundles[0].name)),
    ).pipe(
      filter(isDefined),
      tap(title => this.formGroup.controls.title.setValue(title)),
    );

    this.prefilledTitleSubscription = projectTemplateTitle$.subscribe();

    const projectTemplateSkills$ = Rx.combineLatest([
      Rx.race(
        skillsFromUrl$,
        skillBundles$.pipe(
          map(bundles => bundles[0].skillIds),
          filter(isDefined),
          notEmpty(),
        ),
      ),
      this.skills$.pipe(notEmpty()),
    ]).pipe(
      map(([skillIds, skills]) => {
        const selectedSkills = skillIds
          .map(skillId => skills.find(skill => skill.id === skillId))
          .filter(isDefined);

        return { skills: selectedSkills };
      }),
    );

    this.canRetrieveProjectDetails$ = Rx.race(
      hasRepostProjectError$,
      hasSkillBundlesError$,
    ).pipe(
      map(error => !error),
      startWith(true),
    );

    // This prevents working with drafts when reposting (`repost_project_id`),
    // or creating from a template -- e.g. dashboard templates or crowd favorites
    // (includes a combination of at least one of the ff: `project_title`, `job_id`,
    // `skill_category`, and `skill_subcategory`).
    this.enableDraft$ = this.route.queryParamMap.pipe(
      map(params =>
        params.keys.every(
          key =>
            ![
              'job_id',
              'project_title',
              'repost_contest_id',
              'repost_project_id',
              'skill_category',
              'skill_subcategory',
            ].includes(key),
        ),
      ),
      take(1),
      publishReplay(1),
      refCount(),
    );

    this.draft$ = this.enableDraft$.pipe(
      filter(enableDraft => enableDraft),
      switchMapTo(this.drafts.get()),
      filter(
        (draft): draft is PostJobPageFormState =>
          isDefined(draft) && !this.formGroup.dirty && !this.formGroup.touched,
      ),
      take(1),
      publishReplay(1),
      refCount(),
    );

    this.showSpinner$ = Rx.merge(
      this.enableDraft$.pipe(
        filter(enableDraft => enableDraft),
        switchMap(() => this.drafts.status()),
      ),
      repostProjectDoc.status$,
    ).pipe(map(status => !status.ready && !status.error));

    this.draftSubmitDate$ = this.draft$.pipe(
      map(draft => draft.submitDate),
      filter(isDefined),
    );

    this.showDraftBanner$ = Rx.merge(
      this.draft$.pipe(map(() => true)),
      this.discardDraftClicked$.pipe(map(() => false)),
    );

    this.prefilledFormSubscription = Rx.merge(
      this.draft$,
      repostProject$,
      repostContest$,
    )
      .pipe(
        tap(prefilledForm => {
          if (isDefined(prefilledForm.contestDuration)) {
            this.formGroup.controls.duration.setValue(
              prefilledForm.contestDuration,
            );
          }
          if (isDefined(prefilledForm.contestDurationType)) {
            this.formGroup.controls.durationType.setValue(
              prefilledForm.contestDurationType,
            );
          }
          if (isDefined(prefilledForm.contestPrize)) {
            this.formGroup.controls.contestBudget.setValue(
              prefilledForm.contestPrize,
            );
          }
          if (isDefined(prefilledForm.contestType)) {
            this.formGroup.controls.contestType.setValue(
              prefilledForm.contestType,
            );
          }
          if (isDefined(prefilledForm.currencyId)) {
            this.formGroup.controls.currencyId.setValue(
              prefilledForm.currencyId,
            );
          }
          if (isDefined(prefilledForm.description)) {
            this.formGroup.controls.description.setValue(
              prefilledForm.description,
            );
          }
          if (isDefined(prefilledForm.isLocal)) {
            this.formGroup.controls.isLocal.setValue(null);
          }
          if (isDefined(prefilledForm.jobType)) {
            this.formGroup.controls.jobType.setValue(prefilledForm.jobType);
          }
          if (isDefined(prefilledForm.projectBudgetRange)) {
            this.formGroup.controls.projectBudget.setValue(
              prefilledForm.projectBudgetRange,
            );
          }
          if (isDefined(prefilledForm.projectBudgetType)) {
            this.formGroup.controls.budgetType.setValue(
              prefilledForm.projectBudgetType,
            );
          }
          if (isDefined(prefilledForm.title)) {
            this.formGroup.controls.title.setValue(prefilledForm.title);
          }
          if (prefilledForm.projectBudgetRange?.id === CUSTOM_BUDGET_ID) {
            minBudgetControl.setValue(prefilledForm.projectBudgetRange.minimum);
            maxBudgetControl.setValue(prefilledForm.projectBudgetRange.maximum);
          }
          if (isDefined(prefilledForm.projectType)) {
            this.formGroup.controls.projectType.setValue(
              prefilledForm.projectType,
            );
          }
          if (isDefined(prefilledForm.hasBillingCode)) {
            this.formGroup.controls.hasBillingCode.setValue(
              prefilledForm.hasBillingCode,
            );
          }
          if (isDefined(prefilledForm.billingCode)) {
            this.formGroup.controls.billingCode.setValue(
              prefilledForm.billingCode,
            );
          }

          dirtyAndValidate(this.formGroup);
        }),
      )
      .subscribe();

    const prefilledSkills$ = Rx.merge(
      this.draft$,
      repostProject$,
      projectTemplateSkills$,
      repostContest$,
    ).pipe(
      map(prefilledForm => prefilledForm.skills),
      filter(isDefined),
      notEmpty(),
    );

    this.prefilledSkillsSubscription = Rx.combineLatest([
      prefilledSkills$,
      this.skills$.pipe(notEmpty()),
    ])
      .pipe(
        filter(() => this.formGroup.controls.skills.untouched),
        tap(([prefilledSkills, skills]) => {
          const freshSkills = prefilledSkills
            .map(prefilledSkill =>
              skills.find(skill => skill.id === prefilledSkill.id),
            )
            .filter(
              (prefilledSkill): prefilledSkill is Skill => !!prefilledSkill,
            );

          this.formGroup.controls.skills.setValue(freshSkills);
          this.formGroup.controls.skills.markAsTouched();
        }),
        take(1),
      )
      .subscribe();

    this.draftCreateOrUpdateSubscription = this.enableDraft$
      .pipe(
        filter(enableDraft => enableDraft),
        switchMap(() => this.formGroup.valueChanges),
        this.timeUtils.rxDebounceTime(3000),
        filter(
          () =>
            this.formGroup.controls.title.valid &&
            this.formGroup.controls.description.valid,
        ),
        withLatestFrom(this.userId$),
        switchMap(([form, userId]) => {
          const updatedDraft: PostJobPageFormState = {
            id: toNumber(userId),
            contestDuration: toNumber(form.duration),
            contestDurationType: form.durationType,
            contestPrize: toNumber(form.contestBudget),
            contestType: form.contestType,
            currencyId: form.currencyId,
            description: form.description,
            hasBillingCode: form.hasBillingCode,
            billingCode: form.billingCode,
            isLocal: form.isLocal,
            jobType: form.jobType,
            projectBudgetRange: {
              ...form.projectBudget,
              ...(form.projectBudget &&
              form.projectBudget.id === CUSTOM_BUDGET_ID
                ? {
                    minimum: toNumber(form.customBudget.minimum),
                    maximum: toNumber(form.customBudget.maximum),
                  }
                : {}),
            },
            projectBudgetType: form.budgetType,
            projectType: form.projectType,
            skills: form.skills,
            submitDate: Date.now(),
            title: form.title,
            userId: toNumber(userId),
          };

          if (this.responsePromise) {
            return Rx.of(undefined);
          }

          return this.drafts.save(updatedDraft);
        }),
      )
      .subscribe();

    this.freeUpgradeSubscription = Rx.combineLatest([
      this.projectUpgradeFees$,
      this.discardDraftClicked$.pipe(startWith(undefined)),
      this.draft$.pipe(startWith(undefined)),
    ])
      .pipe(
        filter(
          ([, , draft]) =>
            this.formGroup.controls.projectType.untouched &&
            (isDefined(draft) ||
              this.formGroup.controls[StepName.PROJECT_UPGRADES].pristine),
        ),
      )
      .subscribe(([fees]) => {
        this.formGroup.controls[StepName.PROJECT_UPGRADES].setValue({
          recruiter: fees.freeAssisted,
          nda: fees.freeNda,
          featured: fees.freeFeatured,
          urgent: fees.freeUrgent,
          private: fees.freePrivate,
          sealed: fees.freeSealed,
          ipContract: fees.freeIpContract,
        });
      });
  }

  /**
   * Create the default viewState.
   */
  generateViewStateSkeleton(): ViewState {
    const steps = Object.values(StepName).reduce(
      (acc, stepName) => {
        acc[stepName] = {
          name: stepName,
          visited: stepName === StepName.TITLE_AND_DESCRIPTION,
          show: stepName === StepName.TITLE_AND_DESCRIPTION,
        };
        return acc;
      },
      // we know we're going to add every field since we're `reduce`-ing over every StepName
      // so it's ok to cast as Steps
      {} as Steps,
    );
    return {
      currentStep: steps[StepName.TITLE_AND_DESCRIPTION],
      steps,
      isLocalSkillSelected: false,
      isAllLocalSkillsSelected: false,
      isUrgentSkillSelected: false,
    };
  }

  ngOnDestroy() {
    if (this.contestBudgetValidationSubscription) {
      this.contestBudgetValidationSubscription.unsubscribe();
    }
    if (this.contestMinimumBudgetClassificationSubscription) {
      this.contestMinimumBudgetClassificationSubscription.unsubscribe();
    }
    if (this.customBudgetValidationSubscription) {
      this.customBudgetValidationSubscription.unsubscribe();
    }
    if (this.defaultContestBudgetSubscription) {
      this.defaultContestBudgetSubscription.unsubscribe();
    }
    if (this.defaultCurrencySubscription) {
      this.defaultCurrencySubscription.unsubscribe();
    }
    if (this.detectedLanguageErrorTrackingSubscription) {
      this.detectedLanguageErrorTrackingSubscription.unsubscribe();
    }
    if (this.draftCreateOrUpdateSubscription) {
      this.draftCreateOrUpdateSubscription.unsubscribe();
    }
    if (this.prefilledFormSubscription) {
      this.prefilledFormSubscription.unsubscribe();
    }
    if (this.prefilledSkillsSubscription) {
      this.prefilledSkillsSubscription.unsubscribe();
    }
    if (this.prefilledTitleSubscription) {
      this.prefilledTitleSubscription.unsubscribe();
    }
    if (this.formGroupSubscription) {
      this.formGroupSubscription.unsubscribe();
    }
    if (this.isProjectLocalSubscription) {
      this.isProjectLocalSubscription.unsubscribe();
    }
    if (this.selectedProjectBudgetRangeSubscription) {
      this.selectedProjectBudgetRangeSubscription.unsubscribe();
    }
    if (this.urgentSkillsSubscription) {
      this.urgentSkillsSubscription.unsubscribe();
    }
    if (this.billingCodeSubscription) {
      this.billingCodeSubscription.unsubscribe();
    }
    if (this.freeUpgradeSubscription) {
      this.freeUpgradeSubscription.unsubscribe();
    }
  }

  /**
   * Check if the element is in the current step of the job-post form being
   * being filled out
   *
   * @privateRemarks
   *
   * TODO: Replace `document.getElementById` by using Angular's `ViewChild`
   * [T147935]
   */
  isElementInCurrentStep(targetElement: Element): boolean {
    const currentStepElement = this.document.getElementById(
      this.viewState.currentStep.name,
    );

    if (currentStepElement) {
      return currentStepElement.contains(targetElement);
    }

    return false;
  }

  @HostListener('document:keyup.enter', ['$event.target'])
  @HostListener('document:keyup.control.enter', ['$event.target'])
  handleCtrlEnterKeyboardEvent(target: Element) {
    if (!target) {
      return;
    }

    if (
      (!this.isElementInCurrentStep(target) && target.tagName !== 'BODY') ||
      !this.shouldShowNextButton()
    ) {
      return;
    }

    this.handleNextAction({ stepName: this.viewState.currentStep.name });
  }

  handleNextAction(step: StepValue): void {
    dirtyAndValidate(this.formGroup.controls[step.stepName]);

    const latestShownStep = this.viewState.currentStep.name;

    if (
      step.stepName !== latestShownStep ||
      !this.isStepValid(latestShownStep)
    ) {
      return;
    }

    this.viewState = this.getNewViewState(
      this.viewState,
      this.formGroup,
      FormAction.NEXT,
    );

    this.scrollToNextStep(latestShownStep);
  }

  handleProjectAdvancedUpgradesClicked(showNextButton: boolean) {
    this.projectAdvancedUpgradeShowNextButton = showNextButton;
  }

  handleContestAdvancedUpgradesClicked(showNextButton: boolean) {
    this.contestAdvancedUpgradeShowNextButton = showNextButton;
  }

  scrollToNextStep(currentStep: StepName) {
    const nextStep = this.viewState.steps[
      this.getNextStep(currentStep, this.viewState, this.formGroup)
    ];

    this.scrollToElementId(nextStep.name);
  }

  /**
   * Scroll to an element with a given ID
   *
   * @privateRemarks
   *
   * TODO: Replace `document.getElementById` by using Angular's `ViewChild`
   * [T147935]
   */
  scrollToElementId(targetId: string) {
    // This is to trigger scroll on the next digest cycle
    setTimeout(() => {
      const targetElement = this.document.getElementById(targetId);

      if (!targetElement) {
        return;
      }

      targetElement.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
    });
  }

  shouldShowView(stepName: StepName): boolean {
    const step = this.viewState.steps[stepName];
    return step ? step.show : false;
  }

  isStepValid(stepName: StepName): boolean {
    switch (stepName) {
      case StepName.TITLE_AND_DESCRIPTION:
        return (
          this.formGroup.controls.title.valid &&
          this.formGroup.controls.description.valid &&
          this.formGroup.controls.files.valid &&
          (this.formGroup.controls.descriptionFraudReviews.disabled ||
            this.formGroup.controls.descriptionFraudReviews.valid)
        );
      case StepName.SKILLS:
      case StepName.JOB_TYPE:
      case StepName.PROJECT_IS_LOCAL:
      case StepName.PROJECT_LOCATION:
      case StepName.PROJECT_DATE:
      case StepName.PROJECT_EQUIPMENT:
      case StepName.PROJECT_BUDGET_TYPE:
      case StepName.CONTEST_DURATION_TYPE:
      case StepName.CONTEST_DURATION:
        return this.formGroup.controls[stepName].valid;
      case StepName.PROJECT_BUDGET: {
        const projectBudget = this.formGroup.controls[StepName.PROJECT_BUDGET];
        const isProjectBudgetValid =
          projectBudget.value?.id === CUSTOM_BUDGET_ID
            ? this.formGroup.controls.customBudget.valid
            : projectBudget.valid;
        const isBillingCodeValid = this.formGroup.controls.hasBillingCode.value
          ? this.formGroup.controls.billingCode.valid
          : true;

        return (
          this.formGroup.controls.currencyId.valid &&
          isProjectBudgetValid &&
          isBillingCodeValid
        );
      }
      case StepName.CONTEST_BUDGET:
        return (
          this.formGroup.controls.currencyId.valid &&
          this.formGroup.controls[stepName].valid
        );
      case StepName.PROJECT_UPGRADES:
      case StepName.CONTEST_UPGRADES:
        return (
          this.formGroup.controls[stepName].valid ||
          this.formGroup.controls[stepName].disabled
        );
      case StepName.PROJECT_SUMMARY:
      case StepName.CONTEST_SUMMARY:
        // summary step does not include a `Next` functionality
        return true;
      default:
        return assertNever(stepName);
    }
  }

  /**
   * getNewViewState produce newViewState given the current viewState and form.
   * The only way visited changed to true should be when the FormAction.NEXT is called.
   * FormAction.CHANGE should only change show / hidden. There is however a caveat that
   * you might want to call FormAction.NEXT for certain things (explained more in the function)
   */
  getNewViewState(
    viewState: ViewState,
    form: FormGroup,
    action: FormAction,
    selectedSkills?: ReadonlyArray<Skill>,
  ): ViewState {
    switch (action) {
      case FormAction.NEXT: {
        // Get the next step and mark it as visited + show.
        const nextStep =
          viewState.steps[
            this.getNextStep(viewState.currentStep.name, viewState, form)
          ];
        nextStep.visited = true;
        nextStep.show = true;
        return {
          ...viewState,
          currentStep: nextStep,
        };
      }
      case FormAction.CHANGE: {
        const newViewState = {
          ...viewState,
          isLocalSkillSelected: selectedSkills
            ? this.hasLocalSkill(selectedSkills)
            : viewState.isLocalSkillSelected,
          isAllLocalSkillsSelected: selectedSkills
            ? this.areAllSkillsLocal(selectedSkills)
            : viewState.isAllLocalSkillsSelected,
          isUrgentSkillSelected: selectedSkills
            ? this.hasUrgentSkill(selectedSkills)
            : viewState.isUrgentSkillSelected,
        };

        Object.entries(viewState.steps).forEach(([stepName, step]) => {
          switch (stepName) {
            default:
            case StepName.TITLE_AND_DESCRIPTION:
            case StepName.SKILLS:
            case StepName.JOB_TYPE:
              step.show = step.visited; // Once visited, it won't be hidden anymore
              return;
            case StepName.PROJECT_IS_LOCAL:
              // Only show PROJECT_IS_LOCAL step when there is at least 1 local skill
              step.show =
                (step.visited ||
                  (!step.visited &&
                    (viewState.steps[StepName.PROJECT_LOCATION].visited ||
                      viewState.steps[StepName.PROJECT_BUDGET_TYPE]
                        .visited))) &&
                form.controls[StepName.JOB_TYPE].value === JobType.PROJECT &&
                newViewState.isLocalSkillSelected &&
                !newViewState.isAllLocalSkillsSelected;
              return;
            case StepName.PROJECT_LOCATION:
              // Only show PROJECT_LOCATION step when:
              // there is a local skill and the user selected Yes in local step
              // or all skills are local
              step.show =
                (step.visited ||
                  viewState.steps[StepName.PROJECT_BUDGET_TYPE].visited) &&
                form.controls[StepName.JOB_TYPE].value === JobType.PROJECT &&
                ((form.controls[StepName.PROJECT_IS_LOCAL].value &&
                  newViewState.isLocalSkillSelected) ||
                  newViewState.isAllLocalSkillsSelected);
              return;
            case StepName.PROJECT_DATE:
              step.show =
                (step.visited ||
                  viewState.steps[StepName.PROJECT_UPGRADES].visited) &&
                form.controls[StepName.JOB_TYPE].value === JobType.PROJECT &&
                form.controls[StepName.PROJECT_IS_LOCAL].value;
              return;
            case StepName.PROJECT_EQUIPMENT:
              step.show =
                (step.visited ||
                  viewState.steps[StepName.PROJECT_UPGRADES].visited) &&
                form.controls[StepName.JOB_TYPE].value === JobType.PROJECT &&
                form.controls[StepName.PROJECT_IS_LOCAL].value;
              return;
            case StepName.PROJECT_BUDGET_TYPE:
              step.show =
                (step.visited ||
                  viewState.steps[StepName.PROJECT_BUDGET].visited) &&
                form.controls[StepName.JOB_TYPE].value === JobType.PROJECT;
              return;
            case StepName.PROJECT_BUDGET:
            case StepName.PROJECT_UPGRADES:
            case StepName.PROJECT_SUMMARY:
              // Only show these steps when:
              // No skills selected are locals
              // OR there is a local skill but they answer NO in local step
              // OR there is a local skill and they answer YES in local step and
              //    has entered valid value in project location input field
              step.show =
                step.visited &&
                form.controls[StepName.JOB_TYPE].value === JobType.PROJECT;
              return;
            case StepName.CONTEST_BUDGET:
              // Only when job type === contest
              step.show =
                step.visited &&
                form.controls[StepName.JOB_TYPE].value === JobType.CONTEST;
              return;
            case StepName.CONTEST_DURATION_TYPE:
              // Only show when there is an urgent skill selected
              step.show =
                (step.visited || viewState.steps.contestUpgrades.visited) &&
                form.controls[StepName.JOB_TYPE].value === JobType.CONTEST &&
                newViewState.isUrgentSkillSelected;
              return;
            case StepName.CONTEST_DURATION:
              // Only show when:
              // NO urgent skill is selected
              // OR there is an urgent skill but duration type selected is non-urgent
              step.show =
                (step.visited || viewState.steps.contestUpgrades.visited) &&
                form.controls[StepName.JOB_TYPE].value === JobType.CONTEST &&
                (!newViewState.isUrgentSkillSelected ||
                  (newViewState.isUrgentSkillSelected &&
                    form.controls[StepName.CONTEST_DURATION_TYPE].value ===
                      DurationType.NONURGENT));
              return;
            case StepName.CONTEST_UPGRADES:
            case StepName.CONTEST_SUMMARY:
              // Only show when:
              // NO urgent skill is selected
              // OR there is an urgent skill but duration type selected is non-urgent
              //    and contest duration has been visited (there is a default value already)
              // OR there is an urgent skill and duration type selected is urgent
              step.show =
                step.visited &&
                form.controls[StepName.JOB_TYPE].value === JobType.CONTEST;
          }
        });

        newViewState.currentStep = this.getLatestShownStep(newViewState);
        return newViewState;
      }
      default:
        // NO-OP
        return viewState;
    }
  }
  private getLatestShownStep(viewState: ViewState): Step {
    const name =
      Object.values(StepName)
        .reverse()
        .find(stepName => viewState.steps[stepName].show) ||
      StepName.TITLE_AND_DESCRIPTION;
    return viewState.steps[name];
  }

  private getNextStep(
    currentStepName: String,
    viewState: ViewState,
    form: FormGroup,
  ): StepName {
    switch (currentStepName) {
      case StepName.TITLE_AND_DESCRIPTION:
        return StepName.SKILLS;
      case StepName.SKILLS:
        return StepName.JOB_TYPE;
      case StepName.JOB_TYPE:
      default:
        return form.controls[StepName.JOB_TYPE].value === JobType.PROJECT
          ? this.getProjectNextStep(currentStepName, viewState, form)
          : this.getContestNextStep(currentStepName, viewState, form);
    }
  }

  private getProjectNextStep(
    currentStepName: String,
    viewState: ViewState,
    form: FormGroup,
  ): StepName {
    switch (currentStepName) {
      default:
        if (viewState.isAllLocalSkillsSelected) {
          return StepName.PROJECT_LOCATION;
        }
        if (viewState.isLocalSkillSelected) {
          return StepName.PROJECT_IS_LOCAL;
        }
        return StepName.PROJECT_BUDGET_TYPE;
      case StepName.PROJECT_IS_LOCAL:
        return form.controls[StepName.PROJECT_IS_LOCAL].value === true
          ? StepName.PROJECT_LOCATION
          : StepName.PROJECT_BUDGET_TYPE;

      case StepName.PROJECT_LOCATION:
        return StepName.PROJECT_DATE;

      case StepName.PROJECT_DATE:
        return StepName.PROJECT_EQUIPMENT;

      case StepName.PROJECT_EQUIPMENT:
        return StepName.PROJECT_BUDGET_TYPE;

      case StepName.PROJECT_BUDGET_TYPE:
        return StepName.PROJECT_BUDGET;

      case StepName.PROJECT_BUDGET:
        return StepName.PROJECT_UPGRADES;

      case StepName.PROJECT_UPGRADES:
        return StepName.PROJECT_SUMMARY;
    }
  }

  private getContestNextStep(
    currentStepName: String,
    viewState: ViewState,
    form: FormGroup,
  ): StepName {
    let contestDurationTypeFormControl: AbstractControl | null;
    switch (currentStepName) {
      default:
        return StepName.CONTEST_BUDGET;
      case StepName.CONTEST_BUDGET:
        if (viewState.isUrgentSkillSelected) {
          return StepName.CONTEST_DURATION_TYPE;
        }
        return StepName.CONTEST_DURATION;
      case StepName.CONTEST_DURATION_TYPE:
        contestDurationTypeFormControl = form.get(
          StepName.CONTEST_DURATION_TYPE,
        );
        if (contestDurationTypeFormControl) {
          return contestDurationTypeFormControl.value === DurationType.NONURGENT
            ? StepName.CONTEST_DURATION
            : StepName.CONTEST_UPGRADES;
        }
        return StepName.CONTEST_DURATION;
      case StepName.CONTEST_DURATION:
        return StepName.CONTEST_UPGRADES;
      case StepName.CONTEST_UPGRADES:
        return StepName.CONTEST_SUMMARY;
    }
  }

  getNextButtonShortcut(): ShortcutKey {
    return this.viewState.currentStep.name === StepName.TITLE_AND_DESCRIPTION
      ? ShortcutKey.CTRL_ENTER
      : ShortcutKey.ENTER;
  }

  shouldShowNextButton(): boolean {
    // list of steps where next button should be shown
    const nextButtonShown = [
      StepName.TITLE_AND_DESCRIPTION,
      StepName.SKILLS,
      StepName.PROJECT_LOCATION,
      StepName.PROJECT_DATE,
      StepName.PROJECT_EQUIPMENT,
      StepName.PROJECT_BUDGET,
      StepName.PROJECT_UPGRADES,
      StepName.CONTEST_BUDGET,
      StepName.CONTEST_DURATION,
      StepName.CONTEST_UPGRADES,
    ];
    return !!nextButtonShown.find(stepName => {
      if (
        stepName === StepName.PROJECT_UPGRADES &&
        stepName === this.viewState.currentStep.name
      ) {
        return (
          this.projectAdvancedUpgradeShowNextButton ||
          this.formGroup.controls[StepName.PROJECT_IS_LOCAL].value === true
        );
      }
      if (
        stepName === StepName.CONTEST_UPGRADES &&
        stepName === this.viewState.currentStep.name
      ) {
        return this.contestAdvancedUpgradeShowNextButton;
      }
      return stepName === this.viewState.currentStep.name;
    });
  }

  handleSubmit() {
    dirtyAndValidate(this.formGroup);
    this.formSubmitApiErrorSubject$.next(null);

    const invalidStep = this.getFirstInvalidStep();

    if (invalidStep !== undefined) {
      this.scrollToElementId(invalidStep);
      return;
    }

    this.auth
      .isLoggedIn()
      .pipe(take(1))
      .toPromise()
      .then(isLoggedIn => (isLoggedIn ? true : this.openLoginSignupModal()))
      .then(isLoggedIn => {
        if (!isDefined(isLoggedIn)) {
          return;
        }

        this.isCalifornianUserUsingUnsupportedCurrency$
          .pipe(take(1))
          .toPromise()
          .then(isCalifornianUsingUnsupportedCurrency => {
            if (!isDefined(isCalifornianUsingUnsupportedCurrency)) {
              return;
            }

            if (isCalifornianUsingUnsupportedCurrency) {
              this.formSubmitApiErrorSubject$.next({
                errorCode: 'BLOCKED_CALIFORNIAN_ERROR',
              });
              return;
            }

            if (
              this.formGroup.controls[StepName.JOB_TYPE].value ===
                JobType.PROJECT ||
              this.formGroup.controls[StepName.JOB_TYPE].value === JobType.LOCAL
            ) {
              this.totalFee$
                .pipe(take(1))
                .toPromise()
                .then(totalFee =>
                  this.submitProject(this.formGroup.value, totalFee),
                );
            } else {
              this.submitContest(this.formGroup.value);
            }
          });
      });
  }

  getFirstInvalidStep() {
    return Object.values(StepName).find(
      step => this.viewState.steps[step].show && !this.isStepValid(step),
    );
  }

  createProjectUpgradeCartItem(
    upgradeName: string,
    projectId: number,
    fees: ProjectUpgradeFees,
  ): PartialPaymentsCartItem {
    const upgradeType = transformUpgradeType(upgradeName);

    if (!upgradeType) {
      throw new Error(
        `Cannot create cart item for unknown upgrade type: ${upgradeName}.`,
      );
    }

    return {
      amount: this.projectUpgradesHelper.getUpgradeFee(upgradeType, fees),
      contextId: `${projectId}`,
      contextType: ContextTypeApi.PROJECT_UPGRADE,
      contextSubType: this.projectUpgradesHelper.getContextSubType(upgradeType),
      currencyId: fees.currencyId,
      description: `${getUpgradeName(upgradeType)} Upgrade`,
      useBonus: false,
    };
  }

  createContestPrizeCartItem(
    contestId: number,
    currencyId: number,
    prize: number,
    upgrades: ReadonlyArray<UpgradeType>,
    upgradeFees: ContestFee,
  ): PartialPaymentsCartItem {
    const items = [
      'Contest Prize',
      ...upgrades
        .filter(
          upgrade =>
            upgrade === UpgradeType.URGENT || upgrade === UpgradeType.NDA,
        )
        .map(upgrade => `${getUpgradeName(upgrade)} Upgrade`),
    ];

    return {
      amount:
        prize +
        this.contestUpgradesHelper.getTotalUpgradesFee(upgrades, upgradeFees),
      contextId: `${contestId}`,
      contextType: ContextTypeApi.CONTEST_PRIZE_BUNDLE,
      currencyId,
      description: items.join(', '),
      useBonus: false,
    };
  }

  createContestUpgradeCartItem(
    contestId: number,
    fees: ContestFee,
    upgradeName: string,
  ): PartialPaymentsCartItem {
    const upgradeType = transformUpgradeType(upgradeName);

    if (!upgradeType) {
      throw new Error(
        `Cannot create cart item for unknown upgrade type: ${upgradeName}.`,
      );
    }

    return {
      amount: this.contestUpgradesHelper.getUpgradeFee(upgradeType, fees),
      contextId: `${contestId}`,
      contextSubType: this.contestUpgradesHelper.getContextSubType(upgradeType),
      contextType: ContextTypeApi.CONTEST_UPGRADE,
      currencyId: fees.currencyId,
      description: `${getUpgradeName(upgradeType)} Upgrade`,
      useBonus: false,
    };
  }

  createEquipment(): Promise<ReadonlyArray<Equipment>> {
    let equipment: ReadonlyArray<Equipment> = [];

    return new Promise((resolve, reject) => {
      this.formGroup.value.projectEquipment.forEach((equipmentName: string) => {
        this.datastore
          .createDocument<EquipmentCollection>('equipment', {
            id: Date.now(),
            name: equipmentName,
          })
          .then(response => {
            if (response.status === 'success') {
              equipment = [
                ...equipment,
                {
                  id: response.id,
                  name: equipmentName,
                },
              ];
              if (
                equipment.length ===
                this.formGroup.value.projectEquipment.length
              ) {
                resolve(equipment);
              }
            } else {
              reject(response);
            }
          });
      });
    });
  }

  submitProject(form: any, totalFee: number) {
    this.preloadRoute.preload('app/projects/project-view/project-view.module');

    this.disableForm.emit(true);

    this.responsePromise = Rx.combineLatest([
      this.auth.getUserId(),
      this.currencies$,
    ])
      .pipe(take(1))
      .toPromise()
      .then(([authUid, currencies]) => {
        if (
          form.isLocal !== false &&
          form.location &&
          this.formGroup.value.projectEquipment &&
          this.formGroup.value.projectEquipment.length > 0
        ) {
          return this.createEquipment()
            .then(equipment => ({
              authUid,
              currencies,
              equipment,
            }))
            .catch(response => {
              throw new Error(JSON.stringify(response));
            });
        }
        return { authUid, currencies, equipment: [] };
      })
      .then(({ authUid, currencies, equipment }) =>
        this.datastore.createDocument<ProjectViewProjectsCollection>(
          'projectViewProjects',
          {
            title: form.title,
            description: form.description,
            currency: currencies.find(
              currency => currency.id === toNumber(form.currencyId),
            ) || {
              id: toNumber(form.currencyId),
              code: '',
              sign: '',
            },
            budget: {
              ...(form.projectBudget.id === CUSTOM_BUDGET_ID
                ? {
                    minimum: toNumber(form.customBudget.minimum),
                    maximum: toNumber(form.customBudget.maximum),
                  }
                : form.projectBudget),
              currencyId: toNumber(form.currencyId),
            },
            billingCode: form.hasBillingCode ? form.billingCode : undefined,
            skills: form.skills,
            type: form.budgetType,
            hireme: false,
            language: form.language,
            location:
              form.isLocal !== false && form.location
                ? {
                    administrativeArea:
                      form.location.location.administrativeArea,
                    country: form.location.location.country,
                    fullAddress: form.location.location.fullAddress,
                    mapCoordinates: {
                      latitude: toNumber(form.location.latitude),
                      longitude: toNumber(form.location.longitude),
                    },
                    vicinity: form.location.location.vicinity,
                  }
                : undefined,
            localDetails:
              form.isLocal !== false
                ? {
                    endLocation: form.endLocation
                      ? {
                          administrativeArea:
                            form.endLocation.location.administrativeArea,
                          country: form.endLocation.location.country,
                          fullAddress: form.endLocation.location.fullAddress,
                          mapCoordinates: {
                            latitude: toNumber(form.endLocation.latitude),
                            longitude: toNumber(form.endLocation.longitude),
                          },
                          vicinity: form.endLocation.location.vicinity,
                        }
                      : undefined,
                    date: form.projectDate
                      ? {
                          year: form.projectDate.getFullYear(),
                          month: form.projectDate.getMonth(),
                          day: form.projectDate.getDate(),
                        }
                      : undefined,
                  }
                : undefined,
            local: form.isLocal !== false && form.location,
            equipment: equipment.length === 0 ? undefined : equipment,
            hourlyProjectInfo:
              form.budgetType === BudgetType.HOURLY
                ? {
                    commitment: {
                      hours: 40,
                      interval: TimeUnitApi.WEEK,
                    },
                  }
                : undefined,
            upgrades: {
              ...(totalFee > 0
                ? // will be applied after processing cart instead
                  {
                    assisted: false,
                    featured: false,
                    NDA: false,
                    urgent: false,
                    nonpublic: false,
                    sealed: false,
                    ipContract: false,
                  }
                : // will be applied on project creation
                  {
                    assisted: form.projectUpgrades.recruiter,
                    featured: form.projectUpgrades.featured,
                    NDA: form.projectUpgrades.nda,
                    urgent: form.projectUpgrades.urgent,
                    nonpublic: form.projectUpgrades.private,
                    sealed: form.projectUpgrades.sealed,
                    ipContract: form.projectUpgrades.ipContract,
                  }),

              /**
               * The Recruiter upgrade will be applied after cart HOWEVER due
               * to the fact that the webapp PJP creates and posts the project
               * before processing the cart there is a conflict with the Free
               * Recruiter upgrade which is calculated on project creation.
               */
              unpaidRecruiter: form.projectUpgrades
                ? form.projectUpgrades.recruiter
                : false,

              // not available for purchase on the PJP
              fulltime: false,
              listed: false,
              projectManagement: false,
              qualified: false,
              extend: false,
            },
            files: this.fileIds.map(id => ({ id })),

            // FIXME: Make this the real values
            ndaDetails: { signatures: [] },
          },
        ),
      )
      .then(response => {
        if (response.status === 'success') {
          this.is5EyesUser.then(is5Eyes => {
            if (is5Eyes) {
              // T151438 GA and Facebook Pixel conversion tracking AB test.
              // https://www.freelancercom/admin/abtest/index.php?action=show_experiment&experiment_id=1075
              this.abTest
                .getUserExperimentVariation(
                  'T151438-ga-fb-conversion-tracking-v1',
                )
                .pipe(take(1))
                .toPromise()
                .then(abTestVariation => {
                  // Calling the trackCustomEvent function instead of using `flTrackingConversion`
                  // as a temporary solution before we clean up the AB test because we have to enrol
                  // the user into the AB test right before we send the tracking event.
                  this.tracking.trackCustomEvent(
                    'Post Non Hire Me Project',
                    'PJPPostCTA',
                    {
                      skills: this.formGroup.controls.skills.value.map(
                        (skill: Skill) => skill.name,
                      ),
                      ab_test_variation: `T151438-ga-fb-conversion-tracking-v1::${abTestVariation}`,
                    },
                  );
                });
            }

            this.deleteDraft();

            const upgrades = this.getSelectedUpgrades(form.projectUpgrades);

            if (upgrades && upgrades.length > 0 && totalFee > 0) {
              this.processProjectUpgrades(response.id, upgrades).then(
                cartResponse => {
                  if (cartResponse.status === 'error') {
                    this.disableForm.emit(false);

                    this.formSubmitApiErrorSubject$.next({
                      contextId: response.id,
                      errorCode: 'CART_PAYMENT_ERROR',
                      requestId: cartResponse.requestId,
                    });
                  }
                },
              );
            } else {
              this.router.navigate([`/projects/${response.id}`], {
                fragment: '/proposals',
              });
            }
          });
        } else {
          throw new Error(
            JSON.stringify({
              errorCode: response.errorCode,
              requestId: response.requestId,
            }),
          );
        }
      })
      .catch(e => {
        this.disableForm.emit(false);
        this.formSubmitApiErrorSubject$.next(JSON.parse(e.message));
        return JSON.parse(e.message);
      });
  }

  getSelectedUpgrades(
    upgrades: ContestUpgrade | ProjectAdvancedUpgrades,
  ): ReadonlyArray<string> {
    if (!upgrades) {
      return [];
    }

    return Object.entries(upgrades)
      .filter(([_, value]) => value)
      .map(([key, _]) => key);
  }

  processProjectUpgrades(projectId: number, upgrades: ReadonlyArray<string>) {
    return this.projectUpgradeFees$
      .pipe(take(1))
      .toPromise()
      .then(upgradeFees => {
        const cartItems = upgrades.map(upgrade =>
          this.createProjectUpgradeCartItem(upgrade, projectId, upgradeFees),
        );
        return this.cart.handle(
          `PJP Cart for Project ${projectId}`,
          {
            destination: DestinationApi.PROJECT_VIEW_PAGE,
            payload: `${projectId}`,
          },
          cartItems,
        );
      });
  }

  submitContest(form: any) {
    this.disableForm.emit(true);

    this.responsePromise = this.datastore.createDocument<ContestsCollection>(
      'contests',
      {
        currency: {
          id: toNumber(form.currencyId),
          code: '',
          sign: '',
        },
        description: form.description,
        draft: true,
        duration:
          form.durationType === DurationType.URGENT
            ? URGENT_CONTEST_DURATION
            : toNumber(form.duration),
        fileIds: this.fileIds,
        skills: form.skills,
        prize: toNumber(form.contestBudget),
        title: form.title,
        type: form.contestUpgrades.guaranteed
          ? ContestTypeApi.GUARANTEED
          : ContestTypeApi.PREPAID,
        upgrades: {
          // We create the draft contest and set the upgrades to false except for
          // NDA/URGENT upgrade. This is because we can only set them on draft
          // contests. Check `ContestPrizeBundleCartItem` to find out how we
          // handle NDA/URGENT upgrade along with the prize.
          featured: false,
          highlight: false,
          nda: form.contestUpgrades.nda,
          private: false,
          sealed: false,
          topContest: false,
          urgent: form.contestUpgrades.urgent,

          // property required by the model but not necessary when setting the
          // contest to guaranteed
          guaranteed: false,
        },

        // Dummy data. FIXME: Make this the real current user id
        ownerId: 0,
      },
    );
    this.responsePromise
      .then(response => {
        if (response.status === 'success') {
          this.is5EyesUser.then(is5Eyes => {
            if (is5Eyes) {
              // T151438 GA and Facebook Pixel conversion tracking AB test.
              // https://www.freelancercom/admin/abtest/index.php?action=show_experiment&experiment_id=1075
              this.abTest
                .getUserExperimentVariation(
                  'T151438-ga-fb-conversion-tracking-v1',
                )
                .pipe(take(1))
                .toPromise()
                .then(abTestVariation => {
                  // Calling the trackCustomEvent function instead of using `flTrackingConversion`
                  // as a temporary solution before we clean up the AB test because we have to enrol
                  // the user into the AB test right before we send the tracking event.
                  this.tracking.trackCustomEvent('Post Contest', 'PJPPostCTA', {
                    skills: this.formGroup.controls.skills.value.map(
                      (skill: Skill) => skill.name,
                    ),
                    ab_test_variation: `T151438-ga-fb-conversion-tracking-v1::${abTestVariation}`,
                  });
                });
            }

            this.deleteDraft();

            return this.processContestPrizeAndUpgrades(
              response.id,
              toNumber(form.contestBudget),
              toNumber(form.currencyId),
              this.getSelectedUpgrades(form.contestUpgrades),
            ).then(cartResponse => {
              if (cartResponse.status === 'error') {
                this.disableForm.emit(false);

                this.formSubmitApiErrorSubject$.next({
                  contextId: response.id,
                  errorCode: 'CART_PAYMENT_ERROR',
                  requestId: cartResponse.requestId,
                });
              }
            });
          });
        } else {
          this.disableForm.emit(false);

          // TODO - handle minimum prize validation error from back-end (T83125)
          throw new Error(
            JSON.stringify({
              errorCode: response.errorCode,
              requestId: response.requestId,
            }),
          );
        }
      })
      .catch(e => {
        this.formSubmitApiErrorSubject$.next(JSON.parse(e.message));
      });
  }

  processContestPrizeAndUpgrades(
    contestId: number,
    contestPrize: number,
    currencyId: number,
    upgrades: ReadonlyArray<string>,
  ) {
    return this.contestUpgradeFees$
      .pipe(take(1))
      .toPromise()
      .then(upgradeFees => {
        const contestPrizeItem = this.createContestPrizeCartItem(
          contestId,
          currencyId,
          contestPrize,
          upgrades
            .filter(
              (upgrade: string) =>
                upgrade === UpgradeType.NDA || upgrade === UpgradeType.URGENT,
            )
            .map(transformUpgradeType)
            .filter(isDefined),
          upgradeFees,
        );
        const upgradeItems = upgrades
          // this excludes Guaranteed, NDA, and Urgent upgrade from being added
          // to the cart items. Guaranteed upgrade is free and it's not an
          // upgrade. In T80831, guaranteed upgrade should have it's own form
          // control so we can remove the filter below. In T103755, we decided
          // to process NDA and Urgent upgrade along with the prize as a bundle.
          // It would make cart fail if we try to pass it as a contest upgrade
          // item.
          .filter(
            (upgrade: string) =>
              upgrade !== UpgradeType.GUARANTEED &&
              upgrade !== UpgradeType.NDA &&
              upgrade !== UpgradeType.URGENT,
          )
          .map(upgrade =>
            this.createContestUpgradeCartItem(contestId, upgradeFees, upgrade),
          );

        return [...upgradeItems, contestPrizeItem];
      })
      .then(contestUpgradeItems =>
        this.cart.handle(
          `PJP Cart for Contest ${contestId}`,
          {
            destination: DestinationApi.CONTEST_VIEW_PAGE,
            payload: `${contestId}/details`,
          },
          contestUpgradeItems,
        ),
      );
  }

  hasUrgentSkill(skills: ReadonlyArray<Skill>): boolean {
    return !!skills.find(
      skill =>
        !!(skill.category && skill.category.id === SkillCategoryId.URGENT),
    );
  }

  areAllSkillsLocal(skills: ReadonlyArray<Skill>): boolean {
    return skills.length > 0 && skills.every(skill => skill.local);
  }

  hasLocalSkill(skills: ReadonlyArray<Skill>): boolean {
    return !!skills.find(skill => skill.local);
  }

  handleUploadedFilesChange(fileIds: ReadonlyArray<number>) {
    this.fileIds = fileIds;
  }

  handleDiscardDraft() {
    this.formGroup.reset(DEFAULT_FORM_VALUES);
    this.viewState = this.generateViewStateSkeleton();
    this.discardDraftClickedSubject$.next();

    return this.deleteDraft();
  }

  deleteDraft() {
    return this.enableDraft$
      .pipe(take(1))
      .toPromise()
      .then(isDraftEnabled => {
        if (isDraftEnabled) {
          this.drafts.remove();
        }
      });
  }

  openLoginSignupModal() {
    return this.modalService
      .open(LoginSignupModalComponent, {
        closeable: true,
        edgeToEdge: true,
        inputs: {
          forceAccountType: RoleApi.EMPLOYER,
          form: LoginOrSignup.SIGNUP,
        },
        size: ModalSize.XSMALL,
      })
      .afterClosed()
      .toPromise();
  }
}
